import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase, handleVisibilityChange, reinitializeSupabaseClient, diagnoseAndFixConnection, getSupabaseConnectionState } from '../lib/supabase';
import { createClient } from '@supabase/supabase-js';
import { useAuth } from '../contexts/AuthContext';
import { PostgrestResponse } from '@supabase/postgrest-js';

export interface Room {
  id: string;
  name: string;
  owner_id: string;
  is_public: boolean;
  viewers: number;
  video_url?: string;
  created_at?: string;
  last_active?: string;
  owner_name?: string;
  last_updated?: string;
}

// Funciones wrapper seguras para RPCs
export const safeIncrementViewers = async (roomId: string | undefined | null) => {
  if (!roomId) {
    console.error("[safeIncrementViewers] Intento de llamada con roomId inválido.");
    return false;
  }
  try {
    const { error } = await supabase.rpc('increment_viewers', { room_id: roomId });
    if (error) throw error;
    return true;
  } catch (error: any) {
    console.error(`[safeIncrementViewers] Error RPC para sala ${roomId}:`, {
      message: error?.message,
      details: error?.details,
      hint: error?.hint,
      code: error?.code
    });
    return false;
  }
};

export const safeDecrementViewers = async (roomId: string | undefined | null) => {
  if (!roomId) {
    console.error("[safeDecrementViewers] Intento de llamada con roomId inválido.");
    return false;
  }
   try {
    const { error } = await supabase.rpc('decrement_viewers', { room_id: roomId });
    if (error) throw error;
    return true;
  } catch (error: any) {
    console.error(`[safeDecrementViewers] Error RPC para sala ${roomId}:`, {
      message: error?.message,
      details: error?.details,
      hint: error?.hint,
      code: error?.code
    });
    return false;
  }
};

// Función segura para realizar upsert con manejo de errores
export const safeUpsert = async (table: string, data: any, onConflict?: string) => {
  // Añadir validación de datos básicos
  if (!data || typeof data !== 'object') {
    console.error(`[safeUpsert] Intento de upsert con datos inválidos en tabla ${table}:`, data);
    return false;
  }
  // Asegurar que la clave de conflicto exista si se proporciona
  if (onConflict && !onConflict.split(',').every(key => key.trim() in data)) {
     console.error(`[safeUpsert] Datos inválidos para onConflict=${onConflict} en tabla ${table}:`, data);
     return false;
  }

  try {
    const { error } = await supabase
      .from(table)
      .upsert(data, { onConflict });

    if (error) throw error;
    return true;
  } catch (error: any) {
    // Loguear error detallado
    console.error(`[safeUpsert] Error ${error?.code || 'desconocido'} en ${table}:`, {
      message: error?.message,
      details: error?.details,
      hint: error?.hint,
      dataSent: data // Incluir datos enviados para depuración
    });
    return false;
  }
};

export const useRooms = () => {
  const [publicRooms, setPublicRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const previousRoomsRef = useRef<string>('');
  const { user } = useAuth();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isFetchingRef = useRef<boolean>(false);
  // Referencia para rastrear si hay una operación de visibilidad en curso
  const isVisibilityOperationInProgressRef = useRef<boolean>(false);

  const fetchPublicRooms = useCallback(async () => {
    if (isFetchingRef.current) {
      console.log("[fetchPublicRooms] Log 0: Ya hay una búsqueda en curso, omitiendo.");
      return;
    }
    isFetchingRef.current = true;
    console.log("[fetchPublicRooms] Log 1: Iniciando (marcado como ocupado)...");
    try {
      setLoading(true);
      setError(null);
      console.log("[fetchPublicRooms] Log 2: Estado de carga iniciado.");

      // PASO 1: Obtener salas con tiempo límite
      console.log("[fetchPublicRooms] Log 3: Antes de consultar salas...");

      // Establecer un tiempo límite para la consulta de salas
      const roomsPromise = supabase
        .from('rooms')
        .select(`
          id,
          name,
          owner_id,
          is_public,
          viewers,
          video_url,
          created_at,
          last_active
        `)
        .eq('is_public', true)
        .gt('last_active', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('last_active', { ascending: false })
        .limit(10);

      // Implementar un mecanismo de reintento con backoff exponencial
      let retryCount = 0;
      const maxRetries = 3;
      const baseTimeout = 5000; // 5 segundos iniciales

      const fetchWithRetry = async (): Promise<{data: any[] | null, error: Error | null}> => {
        while (retryCount < maxRetries) {
          try {
            console.log(`[fetchPublicRooms] Intento ${retryCount + 1}/${maxRetries} de obtener salas...`);
            const timeout = baseTimeout * Math.pow(2, retryCount); // Backoff exponencial

            // Crear una promesa con timeout
            const timeoutPromise = new Promise<{data: null, error: Error}>((resolve) => {
              setTimeout(() => {
                console.log(`[fetchPublicRooms] Timeout de ${timeout}ms alcanzado en intento ${retryCount + 1}`);
                resolve({data: null, error: new Error(`Timeout en intento ${retryCount + 1}`)});
              }, timeout);
            });

            // Intentar obtener las salas con timeout
            const result = await Promise.race([roomsPromise, timeoutPromise]);

            // Si hay datos o es el último intento, devolver el resultado
            if (result.data || retryCount === maxRetries - 1) {
              return result;
            }

            // Si no hay datos y no es el último intento, incrementar el contador y reintentar
            retryCount++;
            console.log(`[fetchPublicRooms] Reintentando (${retryCount}/${maxRetries})...`);
          } catch (err) {
            console.error(`[fetchPublicRooms] Error en intento ${retryCount + 1}:`, err);
            if (retryCount === maxRetries - 1) {
              return {data: [], error: err as Error};
            }
            retryCount++;
          }
        }

        // Si llegamos aquí, todos los intentos fallaron
        console.log("[fetchPublicRooms] Todos los intentos fallaron, devolviendo lista vacía");
        return {data: [], error: new Error('Todos los intentos de obtener salas fallaron')};
      };

      // Ejecutar la función de reintento
      const { data: roomsData, error: roomsError } = await fetchWithRetry();

      console.log("[fetchPublicRooms] Log 4: Después de consultar salas.");

      // Manejar el resultado de la operación de obtener salas
      if (roomsError) {
        console.log("[fetchPublicRooms] Error al obtener salas:", roomsError);

        // Mostrar un mensaje amigable al usuario
        if (roomsError.message && roomsError.message.includes('Timeout')) {
          console.log("[fetchPublicRooms] Error de timeout detectado, continuando con datos existentes o lista vacía");

          // No mostrar error al usuario para errores de timeout
          // Intentar usar los datos existentes si los hay
          if (publicRooms.length > 0) {
            console.log("[fetchPublicRooms] Usando datos existentes de salas");
            // No actualizar la UI, mantener los datos existentes
            setLoading(false);
            return;
          }
        }
      }

      // Si llegamos aquí, o bien no hubo error, o bien hubo un error pero no hay datos existentes
      // Usar los datos obtenidos o un array vacío si no hay datos
      const finalRoomsData = roomsData || [];
      console.log(`[fetchPublicRooms] Log 6: Salas obtenidas: ${finalRoomsData.length}`);

      if (finalRoomsData.length === 0) {
        if (previousRoomsRef.current !== '[]') {
          console.log("[fetchPublicRooms] Log 7a: No hay salas, actualizando a vacío.");
          setPublicRooms([]);
          previousRoomsRef.current = '[]';
        }
        console.log("[fetchPublicRooms] Log 7b: No hay salas, antes de setLoading(false).");
        setLoading(false);
        console.log("[fetchPublicRooms] Log 7c: No hay salas, DESPUÉS de setLoading(false).");
        return;
      }

      // PASO 2: Obtener perfiles
      const ownerIds = [...new Set(finalRoomsData.map(room => room.owner_id).filter(Boolean))];
      let profilesMap = new Map<string, string>();

      if (ownerIds.length > 0) {
        console.log(`[fetchPublicRooms] Log 8: Antes de consultar perfiles (${ownerIds.length})...`);

        // Implementar un mecanismo de reintento para obtener perfiles
        let profileRetryCount = 0;
        const profileMaxRetries = 2;
        const profileBaseTimeout = 3000; // 3 segundos iniciales

        const fetchProfilesWithRetry = async (): Promise<{data: any[] | null, error: Error | null}> => {
          while (profileRetryCount < profileMaxRetries) {
            try {
              console.log(`[fetchPublicRooms] Intento ${profileRetryCount + 1}/${profileMaxRetries} de obtener perfiles...`);
              const timeout = profileBaseTimeout * Math.pow(2, profileRetryCount); // Backoff exponencial

              // Crear una promesa con timeout
              const timeoutPromise = new Promise<{data: [], error: Error}>((resolve) => {
                setTimeout(() => {
                  console.log(`[fetchPublicRooms] Timeout de ${timeout}ms alcanzado en intento de perfiles ${profileRetryCount + 1}`);
                  resolve({data: [], error: new Error(`Timeout en intento de perfiles ${profileRetryCount + 1}`)});
                }, timeout);
              });

              // Intentar obtener los perfiles con timeout
              const profilesPromise = supabase
                .from('profiles')
                .select('id, username')
                .in('id', ownerIds);

              const result = await Promise.race([profilesPromise, timeoutPromise]);

              // Si hay datos o es el último intento, devolver el resultado
              if (result.data || profileRetryCount === profileMaxRetries - 1) {
                return result;
              }

              // Si no hay datos y no es el último intento, incrementar el contador y reintentar
              profileRetryCount++;
              console.log(`[fetchPublicRooms] Reintentando perfiles (${profileRetryCount}/${profileMaxRetries})...`);
            } catch (err) {
              console.error(`[fetchPublicRooms] Error en intento de perfiles ${profileRetryCount + 1}:`, err);
              if (profileRetryCount === profileMaxRetries - 1) {
                return {data: [], error: err as Error};
              }
              profileRetryCount++;
            }
          }

          // Si llegamos aquí, todos los intentos fallaron
          console.log("[fetchPublicRooms] Todos los intentos de obtener perfiles fallaron, continuando sin ellos");
          return {data: [], error: new Error('Todos los intentos de obtener perfiles fallaron')};
        };

        // Ejecutar la función de reintento para perfiles
        const { data: profilesData, error: profilesError } = await fetchProfilesWithRetry();

        console.log("[fetchPublicRooms] Log 9: Después de consultar perfiles.");

        if (profilesError) {
          console.error("[fetchPublicRooms] Log 10: Error al obtener perfiles (continuando):", profilesError);
        } else {
          console.log(`[fetchPublicRooms] Log 11: Perfiles obtenidos: ${profilesData?.length ?? 0}`);
          profilesMap = new Map(profilesData.map(p => [p.id, p.username || 'Anónimo']));
        }
      }

      // PASO 3: Combinar datos
      console.log("[fetchPublicRooms] Log 12: Antes de formatear salas...");
      const formattedRooms: Room[] = finalRoomsData.map(room => ({
        id: room.id,
        name: room.name || room.id,
        owner_id: room.owner_id,
        is_public: room.is_public,
        viewers: Math.max(0, room.viewers || 0),
        video_url: room.video_url,
        created_at: room.created_at,
        last_active: room.last_active,
        owner_name: profilesMap.get(room.owner_id) || 'Usuario desconocido',
      }));
      console.log("[fetchPublicRooms] Log 13: Después de formatear salas.");

      // PASO 4: Actualizar estado
      console.log("[fetchPublicRooms] Log 14: Antes de comparar/actualizar estado...");
      const currentDataString = JSON.stringify(formattedRooms);
      if (previousRoomsRef.current !== currentDataString) {
        console.log("[fetchPublicRooms] Log 14a: Detectados cambios, actualizando estado.");
        setPublicRooms(formattedRooms);
        previousRoomsRef.current = currentDataString;
      } else {
        console.log("[fetchPublicRooms] Log 14b: Sin cambios detectados.");
      }
      console.log("[fetchPublicRooms] Log 15: Después de comparar/actualizar estado.");

    } catch (err: any) {
      console.error('[fetchPublicRooms] Log 16: Error CAPTURADO en bloque catch:', err);

      // Manejar errores de timeout de manera especial
      if (err.message && (err.message.includes('Timeout') || err.message.includes('tiempo de espera'))) {
        console.log("Error de tiempo de espera detectado, intentando continuar con datos existentes...");
        // No mostrar error al usuario para errores de timeout, solo registrar
        // Intentar usar los datos existentes si los hay
        if (publicRooms.length === 0) {
          setError("La carga de salas está tardando más de lo esperado. Intenta refrescar la página.");
        }
      } else {
        // Para otros errores, mostrar mensaje al usuario
        setError(err.message || 'Error al cargar las salas públicas');
      }
    } finally {
      console.log("[fetchPublicRooms] Log 17: Ejecutando finally, estableciendo loading a false.");
      setLoading(false);

      // Asegurarse de que la referencia de obtención se libere incluso en caso de error
      setTimeout(() => {
        if (isFetchingRef.current) {
          console.log("[fetchPublicRooms] Liberando referencia de obtención que podría estar bloqueada.");
          isFetchingRef.current = false;
        }
      }, 10000); // Liberar después de 10 segundos si no se ha liberado ya

      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Variable para controlar si la suscripción está activa
    let isSubscriptionActive = false;
    let roomsSubscription: any = null;

    // Función para configurar la suscripción a salas
    const setupSubscription = async () => {
      try {
        // Si ya hay una suscripción activa, eliminarla primero
        if (roomsSubscription) {
          console.log("Eliminando suscripción existente antes de crear una nueva");
          await supabase.removeChannel(roomsSubscription);
          roomsSubscription = null;
        }

        // Obtener la clave anon explícitamente para la configuración del canal
        // @ts-ignore - Ignorar errores de TypeScript para acceder a import.meta.env
        const apiKey = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SUPABASE_ANON_KEY || '';
        if (!apiKey) {
          throw new Error('VITE_SUPABASE_ANON_KEY no encontrada para la suscripción');
        }

        // Configuración robusta del canal, asegurando la clave API
        const channelConfig = {
          config: {
            broadcast: { ack: true },
            presence: { key: `${user?.id || 'anon'}-${Date.now()}` },
            realtime: {
              params: {
                apikey: apiKey // Asegurar que la clave se pasa a los parámetros de Realtime
              }
            }
          }
        };

        console.log("Configurando suscripción a salas con API key:", apiKey.substring(0, 10) + "...");

        // Crear canal usando la instancia global 'supabase' y la configuración
        roomsSubscription = supabase
          .channel('public:rooms', channelConfig)
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'rooms'
          }, (payload) => {
            console.log('Cambio detectado en salas:', payload);
            // Refrescar la lista de salas públicas de forma segura
            fetchPublicRooms().catch(err => console.error("Error al refrescar salas tras cambio:", err));
          })
          .subscribe((status, err) => {
            console.log(`Estado de la suscripción a salas: ${status}`);
            if (status === 'SUBSCRIBED') {
              console.log('Suscripción a salas activa');
              isSubscriptionActive = true;
            } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              console.error(`Error en canal: ${status}`, err);
              isSubscriptionActive = false;
              // Implementar fallback a polling si la suscripción falla
              if (!pollingIntervalRef.current) {
                 console.log('Iniciando polling como fallback debido a error de canal.');
                 pollingIntervalRef.current = setInterval(() => {
                   fetchPublicRooms().catch(err => console.error("Error en polling de salas:", err));
                 }, 30000); // Polling cada 30 segundos
               }
            }
          });
      } catch (error) {
        console.error('Error crítico al configurar suscripción a salas:', error);
        isSubscriptionActive = false;
        // Iniciar polling si la configuración inicial falla
        if (!pollingIntervalRef.current) {
           console.log('Iniciando polling como fallback debido a error de configuración.');
           pollingIntervalRef.current = setInterval(() => {
             fetchPublicRooms().catch(err => console.error("Error en polling de salas:", err));
           }, 30000);
         }
      }
    };

    // Usar la referencia definida en el nivel superior

    // Función para manejar el cambio de visibilidad de manera no bloqueante
    const handleRoomsVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Si ya hay una operación en curso, no iniciar otra
        if (isVisibilityOperationInProgressRef.current) {
          console.log("Ya hay una operación de visibilidad en curso, omitiendo.");
          return;
        }

        console.log("Pestaña visible de nuevo. Iniciando proceso de reconexión en segundo plano...");

        // Marcar que hay una operación en curso
        isVisibilityOperationInProgressRef.current = true;

        // Establecer un tiempo límite para la operación completa
        const timeoutId = setTimeout(() => {
          console.log("Tiempo de espera agotado para la operación de visibilidad.");
          isVisibilityOperationInProgressRef.current = false;

          // SOLUCIÓN RADICAL: Recargar la página si el tiempo se agota
          console.log("SOLUCIÓN RADICAL: Recargando la página para restaurar la conexión...");
          window.location.reload();
        }, 20000); // 20 segundos de tiempo límite

        // Ejecutar el proceso de reconexión en segundo plano
        (async () => {
          try {
            // --- PASO 1: Diagnosticar y solucionar problemas de conexión ---
            console.log("Diagnosticando problemas de conexión...");
            const diagnosis = await diagnoseAndFixConnection();
            console.log("Resultado del diagnóstico:", diagnosis);

            // Si el cliente está en mal estado después del diagnóstico, recargar la página
            if (diagnosis.clientStatus === 'error') {
              console.log("Cliente en mal estado después del diagnóstico, recargando página...");
              window.location.reload();
              return;
            }

            // --- PASO 2: Manejar la reconexión usando la función centralizada ---
            console.log("Ejecutando proceso de reconexión centralizado...");
            const reconnected = await handleVisibilityChange();
            if (!reconnected) {
              console.error("Error en el proceso de reconexión centralizado");
              // Intentar continuar de todos modos
            }

            // --- PASO 3: Reiniciar suscripción ---
            console.log("Reiniciando suscripción a salas...");
            try {
              // No esperar a que termine setupSubscription
              setupSubscription();
            } catch (subscriptionError) {
              console.error("Error al reiniciar suscripción:", subscriptionError);
              // Continuar con la carga de salas de todos modos
            }

            // --- PASO 4: Refrescar salas ---
            console.log("Refrescando lista de salas públicas...");
            await fetchPublicRooms();

            console.log("Proceso de reconexión completado exitosamente.");

            // --- PASO 5: Verificar si la reconexión fue exitosa ---
            const finalCheck = await diagnoseAndFixConnection();
            if (finalCheck.clientStatus === 'error' || finalCheck.sessionStatus === 'error') {
              console.log("La reconexión no fue completamente exitosa, recargando página...");
              window.location.reload();
              return;
            }
          } catch (err) {
            console.error("Error durante el proceso de reconexión:", err);
            // SOLUCIÓN RADICAL: Recargar la página si hay un error grave
            console.log("Error grave durante la reconexión, recargando página...");
            window.location.reload();
          } finally {
            // Limpiar el timeout y marcar que la operación ha terminado
            clearTimeout(timeoutId);
            isVisibilityOperationInProgressRef.current = false;
          }
        })();
      }
    };

    document.addEventListener('visibilitychange', handleRoomsVisibilityChange);

    // Carga inicial de salas públicas
    fetchPublicRooms();

    // Iniciar la suscripción al montar el componente
    setupSubscription();

    // Limpieza al desmontar
    return () => {
      // Eliminar listener de visibilidad
      document.removeEventListener('visibilitychange', handleRoomsVisibilityChange);

      if (roomsSubscription) {
        supabase.removeChannel(roomsSubscription).catch(err => console.error("Error al remover canal:", err));
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Mantener 'user' si setupSubscription depende de él, si no, quitar dependencias

  // Función para verificar si hay sesiones activas para una sala
  const checkActiveSessionsForRoom = async (roomId: string): Promise<boolean> => {
    // Verificar si hay alguna sesión activa en localStorage para esta sala
    let hasActiveSessionsInLocalStorage = false;

    // Revisar localStorage para sesiones
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('session-') && key.endsWith(`-${roomId}`)) {
        console.log(`Encontrada sesión activa en localStorage para sala ${roomId}: ${key}`);
        hasActiveSessionsInLocalStorage = true;
      }
    });

    // Si hay sesiones en localStorage, terminar la verificación
    if (hasActiveSessionsInLocalStorage) {
      return true;
    }

    // Si no hay sesiones en localStorage, verificar en la base de datos
    try {
      const { count, error } = await supabase
        .from('room_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('room_id', roomId)
        .eq('is_active', true)
        .gt('last_active', new Date(Date.now() - 5 * 60 * 1000).toISOString());

      if (error) {
        console.error(`Error al verificar sesiones activas para sala ${roomId}:`, error);
        return false;
      }

      if (count !== null && count > 0) {
        return true;
      }

      return false;
    } catch (err: any) {
      console.error(`Error al verificar sesiones activas para sala ${roomId}:`, err);
      return false;
    }
  };

  const deleteEmptyRoom = async (roomId: string) => {
    console.log(`Verificando si la sala ${roomId} puede ser eliminada...`);

    try {
      // 1. Verificar si la sala es reciente (menos de 3 minutos)
      const { data: roomData, error: roomError } = await supabase
        .from('rooms')
        .select('created_at, viewers, last_active')
        .eq('id', roomId)
        .single();

      if (roomError) {
        console.error(`Error al obtener datos de sala ${roomId}:`, roomError);
        return;
      }

      // Calcular tiempo desde creación
      const createdAt = new Date(roomData.created_at).getTime();
      const now = Date.now();
      const minutesSinceCreation = (now - createdAt) / (1000 * 60);

      // No eliminar salas recién creadas (menos de 3 minutos)
      if (minutesSinceCreation < 3) {
        console.log(`Sala ${roomId} fue creada hace menos de 3 minutos (${minutesSinceCreation.toFixed(1)} min), no se elimina`);
        return;
      }

      // 2. Verificar sesiones activas en la base de datos
      const { count, error: sessionError } = await supabase
        .from('room_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('room_id', roomId)
        .eq('is_active', true)
        .gt('last_active', new Date(Date.now() - 5 * 60 * 1000).toISOString());

      if (sessionError) {
        console.error(`Error al verificar sesiones activas para sala ${roomId}:`, sessionError);
        return;
      }

      // 3. Verificar sesiones en localStorage
      let hasLocalStorageSession = false;
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('session-') && key.endsWith(`-${roomId}`)) {
          console.log(`Encontrada sesión activa en localStorage para sala ${roomId}: ${key}`);
          hasLocalStorageSession = true;
        }
      });

      // Si hay sesiones activas en cualquier lugar, no eliminar
      if ((count && count > 0) || hasLocalStorageSession) {
        console.log(`Sala ${roomId} tiene ${count || 0} sesiones activas en DB y ${hasLocalStorageSession ? 'tiene' : 'no tiene'} sesiones en localStorage. No se elimina.`);

        // Corregir el contador de viewers si está desincronizado
        if (roomData.viewers === 0 && (count || hasLocalStorageSession)) {
          await supabase
            .from('rooms')
            .update({ viewers: count || 1 })
            .eq('id', roomId);

          console.log(`Corregido contador de viewers para sala ${roomId} a ${count || 1}`);
        }

        return;
      }

      // Si llegamos aquí, la sala está realmente vacía y se puede eliminar
      console.log(`Eliminando sala vacía ${roomId}...`);

      const { error: deleteError } = await supabase
        .from('rooms')
        .delete()
        .eq('id', roomId);

      if (deleteError) {
        console.error(`Error al eliminar sala vacía ${roomId}:`, deleteError);
      } else {
        console.log(`Sala vacía ${roomId} eliminada correctamente`);

        // Actualizar la lista local de salas
        setPublicRooms(prev => {
          const newRooms = prev.filter(room => room.id !== roomId);
          return newRooms;
        });
      }
    } catch (err) {
      console.error(`Error al verificar/eliminar sala ${roomId}:`, err);
    }
  };

  // Función auxiliar para crear una sala con una sesión específica
  const createRoomWithSession = async (roomName: string, isPublic: boolean, session: any) => {
    console.log("[createRoomWithSession] Creando sala con sesión recuperada...");
    try {
      if (!session || !session.user) {
        throw new Error('Sesión inválida para crear sala');
      }

      const roomId = Math.random().toString(36).substring(2, 8);
      console.log(`[createRoomWithSession] Intentando crear sala: ${roomName} (ID: ${roomId}, pública: ${isPublic})`);

      // Establecer un tiempo límite para la inserción
      const insertPromise = supabase.from('rooms').insert({
        id: roomId,
        name: roomName,
        owner_id: session.user.id,
        is_public: isPublic,
        viewers: 1,
        last_active: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

      const insertTimeoutPromise = new Promise<{error: Error}>((resolve) => {
        setTimeout(() => {
          console.log("Tiempo de espera agotado al insertar sala, continuando...");
          resolve({error: new Error('Timeout inserting room')});
        }, 15000);
      });

      const { error } = await Promise.race([
        insertPromise,
        insertTimeoutPromise
      ]);

      if (error) {
        console.error("[createRoomWithSession] Error al insertar sala:", error);
        throw error;
      }

      console.log(`[createRoomWithSession] Inserción exitosa. ID: ${roomId}`);

      // Registrar sesión con tiempo límite
      try {
        await registrarSesionParaSala(roomId, session.user.id);
        console.log(`[createRoomWithSession] Sesión registrada.`);
      } catch (sessionRegError) {
        console.error("[createRoomWithSession] Error al registrar sesión, pero continuando:", sessionRegError);
      }

      const createdRoomData = {
        id: roomId,
        name: roomName,
        owner_id: session.user.id,
        is_public: isPublic,
        viewers: 1,
        last_active: new Date().toISOString(),
        created_at: new Date().toISOString()
      };

      console.log("[createRoomWithSession] Devolviendo datos de sala.");
      return createdRoomData;
    } catch (err) {
      console.error('[createRoomWithSession] Error:', err);
      throw err;
    }
  };

  const createRoom = async (roomName: string, isPublic: boolean = false) => {
    console.log("[createRoom] Log C1: Iniciando...");
    try {
      // --- PASO 1: Verificar la sesión actual primero con tiempo límite ---
      console.log("[createRoom] Verificando sesión existente...");

      // Establecer un tiempo límite para obtener la sesión
      const sessionPromise = supabase.auth.getSession();
      // Implementar un mecanismo de reintento con backoff exponencial para obtener la sesión
      let sessionRetryCount = 0;
      const sessionMaxRetries = 3;
      const sessionBaseTimeout = 5000; // 5 segundos iniciales

      const getSessionWithRetry = async (): Promise<{data: {session: any | null}, error: Error | null}> => {
        while (sessionRetryCount < sessionMaxRetries) {
          try {
            console.log(`[createRoom] Intento ${sessionRetryCount + 1}/${sessionMaxRetries} de obtener sesión...`);
            const timeout = sessionBaseTimeout * Math.pow(2, sessionRetryCount); // Backoff exponencial

            // Crear una promesa con timeout
            const timeoutPromise = new Promise<{data: {session: null}, error: Error}>((resolve) => {
              setTimeout(() => {
                console.log(`[createRoom] Timeout de ${timeout}ms alcanzado en intento de sesión ${sessionRetryCount + 1}`);
                resolve({data: {session: null}, error: new Error(`Timeout en intento de sesión ${sessionRetryCount + 1}`)});
              }, timeout);
            });

            // Intentar obtener la sesión con timeout
            const result = await Promise.race([sessionPromise, timeoutPromise]);

            // Si hay datos o es el último intento, devolver el resultado
            if (result.data?.session || sessionRetryCount === sessionMaxRetries - 1) {
              return result;
            }

            // Si no hay datos y no es el último intento, incrementar el contador y reintentar
            sessionRetryCount++;
            console.log(`[createRoom] Reintentando obtener sesión (${sessionRetryCount}/${sessionMaxRetries})...`);
          } catch (err) {
            console.error(`[createRoom] Error en intento de sesión ${sessionRetryCount + 1}:`, err);
            if (sessionRetryCount === sessionMaxRetries - 1) {
              return {data: {session: null}, error: err as Error};
            }
            sessionRetryCount++;
          }
        }

        // Si llegamos aquí, todos los intentos fallaron
        console.log("[createRoom] Todos los intentos de obtener sesión fallaron, intentando recuperar de localStorage");

        // Intentar recuperar la sesión del localStorage como último recurso
        try {
          const localSession = localStorage.getItem('supabase.auth.token');
          if (localSession) {
            const parsedSession = JSON.parse(localSession);
            if (parsedSession && parsedSession.currentSession && parsedSession.currentSession.user) {
              console.log("[createRoom] Sesión recuperada de localStorage:", parsedSession.currentSession.user.id);
              return {data: {session: parsedSession.currentSession}, error: null};
            }
          }
        } catch (parseError) {
          console.error("[createRoom] Error al parsear sesión de localStorage:", parseError);
        }

        return {data: {session: null}, error: new Error('Todos los intentos de obtener sesión fallaron')};
      };

      // Ejecutar la función de reintento para obtener la sesión
      const { data: { session: currentSession }, error: sessionCheckError } = await getSessionWithRetry();

      if (sessionCheckError) {
        console.error("[createRoom] Error al verificar sesión:", sessionCheckError);

        // Si es un error de timeout, intentar obtener la sesión de otra manera
        if (sessionCheckError.message && sessionCheckError.message.includes('Timeout')) {
          console.log("Error de timeout detectado, intentando obtener sesión de localStorage...");

          // Intentar obtener la sesión del localStorage
          const localSession = localStorage.getItem('supabase.auth.token');
          if (localSession) {
            try {
              const parsedSession = JSON.parse(localSession);
              if (parsedSession && parsedSession.currentSession && parsedSession.currentSession.user) {
                console.log("Sesión recuperada de localStorage:", parsedSession.currentSession.user.id);
                // Usar esta sesión en lugar de la que no se pudo obtener
                const recoveredSession = parsedSession.currentSession;
                // Continuar con la sesión recuperada
                return await createRoomWithSession(roomName, isPublic, recoveredSession);
              }
            } catch (parseError) {
              console.error("Error al parsear sesión de localStorage:", parseError);
            }
          }
        }

        // Si no se pudo recuperar la sesión, lanzar el error
        throw new Error(sessionCheckError.message || 'Error al verificar sesión.');
      }

      // Si no hay sesión activa, es un error crítico
      if (!currentSession || !currentSession.user) {
        console.error("[createRoom] No hay sesión activa");
        throw new Error('No hay sesión activa. Inicia sesión para crear una sala.');
      }

      console.log("[createRoom] Sesión activa verificada:", currentSession.user.id);

      const roomId = Math.random().toString(36).substring(2, 8);
      console.log(`[createRoom] Intentando crear sala: ${roomName} (ID: ${roomId}, pública: ${isPublic})`);

      // --- Inserción directa con la sesión existente con tiempo límite ---
      console.log("[createRoom] Antes de insert utilizando userId:", currentSession.user.id);

      // Usar la función auxiliar para crear la sala con la sesión actual
      return await createRoomWithSession(roomName, isPublic, currentSession);
    } catch (err) {
      console.error('[createRoom] Error CAPTURADO:', err);
      throw err;
    } finally {
      console.log("[createRoom] Completado.");
    }
  };

  // Función auxiliar para registrar sesión de usuario en una sala
  const registrarSesionParaSala = async (roomId: string, userId: string) => {
    try {
      // Registrar sesión en la base de datos
      const sessionRegistered = await safeUpsert('room_sessions', {
          room_id: roomId,
        user_id: userId,
          is_active: true,
          last_active: new Date().toISOString()
      }, 'room_id,user_id');

      if (sessionRegistered) {
        console.log(`Sesión registrada correctamente para sala ${roomId} y usuario ${userId}`);
      } else {
        console.warn(`No se pudo registrar la sesión para sala ${roomId} y usuario ${userId}, pero continuando...`);
      }

      // Registrar también en localStorage para persistencia local
      localStorage.setItem(`session-${userId}-${roomId}`, 'true');

      console.log(`Sesión de usuario ${userId} registrada para sala ${roomId}`);
      return true;
    } catch (error) {
      console.error(`Error al registrar sesión para sala ${roomId} y usuario ${userId}:`, error);
      // Al menos guardar en localStorage
      localStorage.setItem(`session-${userId}-${roomId}`, 'true');
      return false;
    }
  };

  const updateRoomStatus = async (roomId: string, isPublic: boolean) => {
    try {
      if (!user) throw new Error('Debes iniciar sesión para actualizar una sala');

      const { error } = await supabase
        .from('rooms')
        .update({ is_public: isPublic })
        .eq('id', roomId)
        .eq('owner_id', user.id);

      if (error) throw error;

      return true;
    } catch (err: any) {
      console.error('Error al actualizar estado de la sala:', err);
      throw err;
    }
  };

  return {
    publicRooms,
    loading,
    error,
    fetchPublicRooms,
    createRoom,
    updateRoomStatus
  };
};